/**
 * Choose a new password, after arriving from a reset email.
 *
 * The user IS signed in at this point — following the link created a recovery
 * session — which is what lets updateUser() set the password without the old
 * one. That's also why "Cancel" signs out rather than just leaving: walking
 * away from this screen otherwise leaves a live session behind on whatever
 * device opened the email.
 */

import { updatePassword, signOut } from '@/lib/auth';
import { esc, setPageTitle } from '@/lib/dom';
import { signInBrand } from '@/lib/host-branding';
import { authCardHeader, authErrorMessage } from '@/lib/auth-card';
import {
  validateNewPassword, setPasswordCopy, MIN_PASSWORD_LENGTH, type PasswordPurpose,
} from '@/lib/auth-redirect';

export interface UpdatePasswordOptions {
  hostname?: string;
  /** 'invite' for someone accepting an invitation (Phase 24). */
  purpose?: PasswordPurpose;
  onDone: () => void;
  onCancelled: () => void;
}

export function renderUpdatePassword(root: HTMLElement, opts: UpdatePasswordOptions): void {
  const brand = signInBrand(opts.hostname ?? location.hostname);
  const copy = setPasswordCopy(opts.purpose ?? 'recovery');
  setPageTitle(copy.title, brand.titleBase);

  root.innerHTML = `
    <div class="auth">
      <form class="auth-card" id="update-form" novalidate>
        ${authCardHeader(brand, copy.title)}
        ${copy.intro ? `<p class="auth-copy">${esc(copy.intro)}</p>` : ''}
        <label class="field">
          <span>New password</span>
          <input type="password" name="password" autocomplete="new-password"
                 minlength="${MIN_PASSWORD_LENGTH}" required />
        </label>
        <label class="field">
          <span>Confirm new password</span>
          <input type="password" name="confirm" autocomplete="new-password" required />
        </label>
        <p class="auth-copy muted">At least ${MIN_PASSWORD_LENGTH} characters.</p>
        <div class="auth-error" id="update-error" hidden></div>
        <button type="submit" class="btn-primary" id="update-submit">${esc(copy.submit)}</button>
        <button type="button" class="btn-link" data-action="cancel-reset">${esc(copy.cancel)}</button>
      </form>
    </div>`;

  const form = root.querySelector<HTMLFormElement>('#update-form')!;
  const errBox = root.querySelector<HTMLDivElement>('#update-error')!;
  const submit = root.querySelector<HTMLButtonElement>('#update-submit')!;

  const showError = (msg: string): void => {
    errBox.hidden = false;
    errBox.textContent = msg;
  };

  root.querySelectorAll('[data-action="cancel-reset"]').forEach((el) => {
    el.addEventListener('click', async () => {
      try { await signOut(); } catch { /* signing out locally is what matters */ }
      opts.onCancelled();
    });
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    errBox.hidden = true;
    const fd = new FormData(form);
    const password = String(fd.get('password') ?? '');
    const problem = validateNewPassword(password, String(fd.get('confirm') ?? ''));
    if (problem) { showError(problem); return; }

    submit.disabled = true;
    submit.textContent = 'Saving…';
    try {
      await updatePassword(password);
      opts.onDone();
    } catch (err) {
      showError(authErrorMessage(err, 'Could not save the new password.'));
      submit.disabled = false;
      submit.textContent = copy.submit;
    }
  });
}
